import CommonWebParameter from './CommonWebParameter';
import Cookie from './utils/Cookie';
import updateWebSessionIdIfNeeded from './utils/WebSessionId';

const LIBRARY_VERSION = '3.5.0';

/**
 * This class provides data that is commonly used for all log data.
 */
export default class CommonParameter {
  private platform: string;
  private pcid: string;
  private memberSrl?: string;
  private libraryVersion?: string;
  private lang: string;
  private resolution?: string;
  private eventTime: string;
  private commonWebParameter?: CommonWebParameter;
  private webSessionId?: string; // web session id for web stability, uuid format
  private webBuildNo?: string; // build number of docker image

  constructor() {
    this.setLang()
      .setMemberSrl()
      .setPCID()
      .setResolution()
      .setLibraryVersion(LIBRARY_VERSION);
  }

  /**
   * Set the CommonWebParameter.
   *
   * @param commonWebParameter
   * @returns {CommonParameter}
   */
  setCommonWebParameter(commonWebParameter: CommonWebParameter) {
    this.commonWebParameter = commonWebParameter;
    return this;
  }

  /**
   * Set the platform value.
   *
   * @param platform {string} app인 경우 자동으로 값이 설정한 된다.
   *
   * @returns {CommonParameter} instance of CommonParameter
   */
  setPlatform(platform: string) {
    this.platform = platform;
    return this;
  }

  /**
   * Set the eventTime value.
   *
   * @param eventTime
   * @returns {CommonParameter} instance of CommonParameter
   */
  setEventTime(eventTime: string) {
    this.eventTime = eventTime;
    return this;
  }

  /**
   * Set the PCID of the client based on the cookie value (PCID).
   *
   * @returns {CommonParameter} instance of CommonParameter
   */
  setPCID() {
    const pcid = Cookie.getCookie('PCID');
    const regex = new RegExp('^\\d{23}$');
    this.pcid = regex.test(pcid) ? pcid : this.generatePCID();

    return this;
  }

  /**
   * Set memberSrl based on cookie value (member_srl).
   *
   * @returns {CommonParameter} instance of CommonParameter
   */
  setMemberSrl() {
    return this;
    // const memberSrl = Cookie.getCookie('member_srl');
    // const regex = new RegExp('^\\d+$');
    // this.memberSrl = regex.test(memberSrl) ? memberSrl : '';

    // return this;
  }

  /**
   * Set the version of CoupangWebLog.
   *
   * @param libraryVersion CoupangWebLog library version.
   * @returns {CommonParameter} instance of CommonParameter
   */
  setLibraryVersion(libraryVersion: string) {
    this.libraryVersion = libraryVersion;
    return this;
  }

  /**
   * Sets the language of the website.
   *
   * @returns {CommonParameter} instance of CommonParameter
   */
  setLang() {
    this.lang = document.documentElement.lang || (navigator.languages && navigator.languages[0]) || navigator.language;
    return this;
  }

  /**
   * Set resolution information of device.
   *
   * @returns {CommonParameter} instance of CommonParameter
   */
  setResolution() {
    this.resolution = `${window.screen.width}x${window.screen.height}`;
    return this;
  }

  /**
   * Set web session id
   * @returns {CommonParameter} instance of CommonParameter
   */
  setWebSessionId() {
    const webSessionId = updateWebSessionIdIfNeeded();
    this.webSessionId = webSessionId;
    return this;
  }

  /**
   * Set release version
   * @returns {CommonParameter} instance of CommonParameter
   */
  setWebBuildNo(webBuildNo?: string) {
    this.webBuildNo = webBuildNo || 'default';
    return this;
  }

  /**
   * JSON object of CommonParameter
   *
   * @returns {{platform: string, clientId: string, memberSrl: string, libraryVersion: string, locale: string, resolution: string, eventTime: string, webSessionId: string, webBuildNo: string}}
   */
  getJSON(): { common: CommonParamsType } {
    const {
      platform,
      pcid,
      libraryVersion,
      lang,
      resolution,
      eventTime,
      webSessionId,
      webBuildNo,

      commonWebParameter
    } = this;

    let commonWebJSON = {};
    if (commonWebParameter) {
      commonWebJSON = commonWebParameter.getJSON();
    }

    const common: CommonParamsType = {
      platform,
      pcid,
      libraryVersion,
      lang,
      resolution,
      eventTime,
      ...commonWebJSON
    };

    if (webSessionId !== undefined) {
      common.webSessionId = webSessionId;
    }
    if (webBuildNo !== undefined) {
      common.webBuildNo = webBuildNo;
    }

    return { common };
  }

  /**
   * Return library version
   * @returns {string}
   */
  getLibVersion(): string {
    return LIBRARY_VERSION;
  }

  /**
   * Return some common properties which should be set to extra, to be preserved in app.
   * the CommonWebParameter.
   *
   * @param shorten if true, too long url and referrer will be cut off
   * @returns {CommonParameter}
   */
  getExtraWebviewJSON(shorten?: boolean): object {
    const commonWebJSON = this.commonWebParameter.getJSON();
    const extraWebviewJSON = {
      eventTime: this.eventTime,
      pcid: this.pcid,
      libraryVersion: this.getLibVersion(),
      ...commonWebJSON['web']
    };

    if (shorten) {
      const url = extraWebviewJSON.url || '';
      const referrer = extraWebviewJSON.referrer || '';
      if (url.length > 100) {
        extraWebviewJSON.url = url.substring(0, 100) + '......';
      }
      if (referrer.length > 100) {
        extraWebviewJSON.referrer = referrer.substring(0, 100) + '......';
      }
    }
    return extraWebviewJSON;
  }

  /**
   * Generate PCID and set generated value to cookie
   * @returns {string}
   */
  private generatePCID(): string {
    const PCID_LENGTH = 23;
    const date = new Date();
    const generated = [];
    let pcid;

    generated.push(date.getTime());
    generated.push(
      Math.random()
        .toString()
        .substring(2)
    );
    Cookie.setCookie('PCID', (pcid = generated.join('').substring(0, PCID_LENGTH)));

    return pcid;
  }
}
